import { useEffect, useMemo, useState } from 'react';
import { settingsAPI, getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Hash, Save, AlertTriangle, GitBranch, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const sanitizePrefix = (value) => value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5);
const riskTone = {
  alto: 'destructive',
  médio: 'secondary',
  baixo: 'outline',
};

export const DocumentPrefixesTab = () => {
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(true);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getDocumentPrefixes();
      setConfig(response.data);

      const mappedDraft = {};
      (response.data?.prefixes || []).forEach((item) => {
        mappedDraft[item.key] = item.prefix;
      });
      setDraft(mappedDraft);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar prefixos dos documentos'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadDiagnostics = async () => {
      try {
        setDiagnosticsLoading(true);
        const response = await settingsAPI.getDocumentPrefixDiagnostics();
        if (mounted) {
          setDiagnostics(response.data || null);
        }
      } catch (error) {
        if (mounted) {
          setDiagnostics(null);
        }
        toast.error(getErrorMessage(error, 'Erro ao carregar diagnóstico dos códigos documentais'));
      } finally {
        if (mounted) {
          setDiagnosticsLoading(false);
        }
      }
    };
    loadDiagnostics();
    return () => {
      mounted = false;
    };
  }, []);

  const hasChanges = useMemo(() => {
    if (!config?.prefixes) return false;
    return config.prefixes.some((item) => (draft[item.key] || item.default_prefix) !== item.prefix);
  }, [config, draft]);

  const handleChange = (key, value) => {
    setDraft((current) => ({ ...current, [key]: sanitizePrefix(value) }));
  };

  const handleResetDefaults = () => {
    const resetDraft = {};
    (config?.prefixes || []).forEach((item) => {
      resetDraft[item.key] = item.default_prefix;
    });
    setDraft(resetDraft);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await settingsAPI.updateDocumentPrefixes({ prefixes: draft });
      setConfig(response.data);

      const mappedDraft = {};
      (response.data?.prefixes || []).forEach((item) => {
        mappedDraft[item.key] = item.prefix;
      });
      setDraft(mappedDraft);
      toast.success('Prefixos dos documentos actualizados com sucesso');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar prefixos dos documentos'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="document-prefixes-loading">
        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
        A carregar prefixos dos documentos...
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="document-prefixes-tab">
      <Card className="border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.08),_transparent_35%),linear-gradient(180deg,#fff,#f8fafc)]">
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Hash className="h-4 w-4 text-sky-600" />
              Codificação configurável dos documentos
            </CardTitle>
            <CardDescription>
              Defina aqui os prefixos visíveis do ERP, mantendo o formato sequencial `PREFIXO-000001`.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleResetDefaults} data-testid="document-prefixes-reset-defaults">
              Repor padrões
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges || saving} data-testid="document-prefixes-save-button">
              {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Guardar prefixos
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 xl:grid-cols-2" data-testid="document-prefixes-grid">
            {(config?.prefixes || []).map((item) => {
              const currentPrefix = draft[item.key] || item.default_prefix;
              return (
                <div key={item.key} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`document-prefix-row-${item.key}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">{item.label}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                    </div>
                    <Badge variant="outline">Padrão: {item.default_prefix}</Badge>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-[160px_1fr] md:items-center">
                    <Input
                      value={currentPrefix}
                      onChange={(event) => handleChange(item.key, event.target.value)}
                      placeholder={item.default_prefix}
                      maxLength={5}
                      data-testid={`document-prefix-input-${item.key}`}
                    />
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Pré-visualização</p>
                      <p className="mt-1 font-mono text-base font-semibold text-slate-900">
                        {currentPrefix || item.default_prefix}-000001
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="document-prefixes-help-box">
            Altere apenas as letras do prefixo. A sequência numérica continua automática e independente por prefixo configurado.
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm" data-testid="document-prefixes-diagnostics-panel">
        <CardHeader className="px-4 py-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Diagnóstico não-destrutivo: códigos documentais sem hífen
          </CardTitle>
          <CardDescription>
            Inventário real do impacto, dependências, riscos e proposta segura de migração. Nenhuma alteração destrutiva foi aplicada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-4 pb-4 pt-0">
          {diagnosticsLoading ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500" data-testid="document-prefixes-diagnostics-loading">
              A carregar diagnóstico de impacto documental...
            </div>
          ) : diagnostics ? (
            <>
              <div className="grid gap-3 md:grid-cols-4" data-testid="document-prefixes-diagnostics-summary-grid">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Campos rastreados</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{diagnostics.impact_summary?.tracked_fields || 0}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Registos com código</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{diagnostics.impact_summary?.records_with_codes || 0}</p>
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-amber-700">Formato canónico</p>
                  <p className="mt-1 text-xl font-semibold text-amber-900">{diagnostics.impact_summary?.hyphenated_standard || 0}</p>
                  <p className="text-[11px] text-amber-700">prefixo + hífen + sequência</p>
                </div>
                <div className="rounded-2xl border border-sky-200 bg-sky-50 px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-sky-700">Já sem hífen</p>
                  <p className="mt-1 text-xl font-semibold text-sky-900">{diagnostics.impact_summary?.hyphenless_standard || 0}</p>
                  <p className="text-[11px] text-sky-700">estado actual encontrado</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="document-prefixes-impact-map">
                <div className="flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-900">Mapa de impacto por campo</p>
                </div>
                <div className="mt-3 space-y-2">
                  {diagnostics.impact_map?.map((entry) => (
                    <div key={entry.key} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3" data-testid={`document-prefix-impact-${entry.key.replace(/[^a-zA-Z0-9]+/g, '-')}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{entry.label}</p>
                          <p className="text-[11px] text-slate-500">{entry.collection}.{entry.field} · {entry.module}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[10px]">
                          <Badge variant="outline">{entry.total_records} registos</Badge>
                          <Badge variant="outline">{entry.hyphenated_standard} canónicos</Badge>
                          <Badge variant="outline">{entry.legacy_complex} legados</Badge>
                        </div>
                      </div>
                      <div className="mt-2 grid gap-2 lg:grid-cols-[1.15fr_1fr_1fr]">
                        <div className="rounded-xl border border-white bg-white px-3 py-2">
                          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Exemplos reais</p>
                          <p className="mt-1 text-xs text-slate-700">{entry.sample_values?.length ? entry.sample_values.join(' · ') : 'Sem amostras encontradas'}</p>
                        </div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2">
                          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">UI afectada</p>
                          <p className="mt-1 text-xs text-slate-700">{entry.ui_surfaces?.join(' · ')}</p>
                        </div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2">
                          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">APIs / impressão</p>
                          <p className="mt-1 text-xs text-slate-700">{entry.api_surfaces?.join(' · ')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-3" data-testid="document-prefixes-dependencies-grid">
                {Object.entries(diagnostics.dependencies || {}).map(([group, items]) => (
                  <div key={group} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Dependências · {group}</p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-700">
                      {items.map((item) => <li key={item}>• {item}</li>)}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="grid gap-3 xl:grid-cols-2" data-testid="document-prefixes-risks-grid">
                {diagnostics.risks?.map((risk) => (
                  <div key={risk.title} className="rounded-2xl border border-slate-200 bg-white p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900">{risk.title}</p>
                          <Badge variant={riskTone[risk.severity] || 'outline'}>{risk.severity}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-slate-600">{risk.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3" data-testid="document-prefixes-safe-migration-plan">
                <p className="text-sm font-semibold text-emerald-900">Proposta segura de migração</p>
                <p className="mt-1 text-xs text-emerald-800">{diagnostics.safe_migration_proposal?.recommendation}</p>
                <div className="mt-3 grid gap-2 lg:grid-cols-2">
                  {diagnostics.safe_migration_proposal?.phases?.map((phase) => (
                    <div key={phase.phase} className="rounded-xl border border-emerald-200 bg-white px-3 py-2">
                      <p className="text-[11px] font-semibold text-slate-900">{phase.phase}</p>
                      <p className="mt-1 text-xs text-slate-600">{phase.goal}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="document-prefixes-decision-matrix">
                <p className="text-sm font-semibold text-slate-900">Matriz comparativa das estratégias</p>
                <div className="mt-3 space-y-2">
                  {diagnostics.decision_matrix?.map((strategy) => (
                    <div key={strategy.strategy} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3" data-testid={`document-prefix-strategy-${strategy.strategy}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{strategy.title}</p>
                          <p className="text-xs text-slate-600">{strategy.summary}</p>
                        </div>
                        <Badge variant={diagnostics.recommended_strategy?.default_option === strategy.strategy ? 'default' : 'outline'}>
                          {diagnostics.recommended_strategy?.default_option === strategy.strategy ? 'recomendado por defeito' : 'alternativa'}
                        </Badge>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-xl border border-white bg-white px-3 py-2"><strong>Impacto funcional:</strong> {strategy.functional_impact}</div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2"><strong>Impacto técnico:</strong> {strategy.technical_impact}</div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2"><strong>Risco operacional:</strong> {strategy.operational_risk}</div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2"><strong>Risco documental:</strong> {strategy.documentary_risk}</div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2 md:col-span-2"><strong>Pesquisa / filtros / impressão:</strong> {strategy.search_print_impact}</div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2 md:col-span-2"><strong>Documentos já emitidos:</strong> {strategy.issued_documents_impact}</div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2 md:col-span-2"><strong>Integrações / referências internas:</strong> {strategy.integration_impact}</div>
                        <div className="rounded-xl border border-white bg-white px-3 py-2 md:col-span-2"><strong>Rollback:</strong> {strategy.rollback_complexity}</div>
                      </div>
                      <div className="mt-2 rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                        <strong>Quando faz mais sentido:</strong> {strategy.best_when}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.1fr_0.9fr]" data-testid="document-prefixes-executive-decision-grid">
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3" data-testid="document-prefixes-recommendation-card">
                  <p className="text-sm font-semibold text-blue-900">Recomendação final</p>
                  <p className="mt-1 text-xs text-blue-800">
                    Estratégia recomendada: <strong>{diagnostics.recommended_strategy?.recommended_strategy}</strong> · opção por defeito: <strong>{diagnostics.recommended_strategy?.default_option}</strong>
                  </p>
                  <ul className="mt-3 space-y-1 text-xs text-blue-900">
                    {(diagnostics.recommended_strategy?.why || []).map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                  <div className="mt-3 rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs text-slate-700">
                    {diagnostics.recommended_strategy?.decision_note}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="document-prefixes-approval-card">
                  <p className="text-sm font-semibold text-slate-900">Critérios de aprovação / go-no-go</p>
                  <ul className="mt-3 space-y-1 text-xs text-slate-700">
                    {(diagnostics.approval_criteria || []).map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-2" data-testid="document-prefixes-phase-rollout-grid">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="document-prefixes-implementation-phases">
                  <p className="text-sm font-semibold text-slate-900">Checklist de implementação por fase</p>
                  <div className="mt-3 space-y-2">
                    {(diagnostics.implementation_phases || []).map((phase) => (
                      <div key={phase.phase} className="rounded-xl border border-white bg-white px-3 py-2">
                        <p className="text-[11px] font-semibold text-slate-900">{phase.phase}</p>
                        <p className="mt-1 text-xs text-slate-600">{phase.objective}</p>
                        <ul className="mt-2 space-y-1 text-xs text-slate-700">
                          {(phase.checks || []).map((check) => <li key={check}>• {check}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="document-prefixes-rollback-phases">
                  <p className="text-sm font-semibold text-slate-900">Checklist de rollback</p>
                  <div className="mt-3 space-y-2">
                    {(diagnostics.rollback_phases || []).map((phase) => (
                      <div key={phase.phase} className="rounded-xl border border-white bg-white px-3 py-2">
                        <p className="text-[11px] font-semibold text-slate-900">{phase.phase}</p>
                        <ul className="mt-2 space-y-1 text-xs text-slate-700">
                          {(phase.actions || []).map((action) => <li key={action}>• {action}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500" data-testid="document-prefixes-diagnostics-empty">
              Não foi possível carregar o diagnóstico nesta tentativa.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};